/**
 * Componente que verifica si el usuario tiene al menos un restaurante
 * Si no tiene ninguno, lo redirige a crear uno
 */

import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { listRestaurants } from '../services/api';

function RequireRestaurant({ children }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [checking, setChecking] = useState(true);
  const [hasRestaurants, setHasRestaurants] = useState(false);

  useEffect(() => {
    // Re-validar en cada cambio de ruta. Sin esto, tras crear el primer
    // restaurante (navigate /restaurant/new → /restaurant) el guard conserva
    // el estado stale `hasRestaurants=false` de la verificación inicial y
    // devuelve null → whitescreen (NER-4031). Una vez confirmado que hay
    // restaurantes, el check hace short-circuit y no vuelve a fetchear.
    checkRestaurants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const checkRestaurants = async () => {
    try {
      // Si ya estamos en la página de crear restaurante, no verificar.
      // Marcar hasRestaurants=true para que el render post-guardado (redirect
      // a /restaurant) no caiga en el return null de más abajo.
      if (location.pathname === '/restaurant/new') {
        setHasRestaurants(true);
        setChecking(false);
        return;
      }

      // Ya confirmamos que el usuario tiene restaurantes: no re-fetchear en
      // cada navegación (evita un fetch por cada cambio de ruta).
      if (hasRestaurants) {
        setChecking(false);
        return;
      }

      setChecking(true);
      const response = await listRestaurants();
      const restaurants = response.restaurants || [];

      console.log('[RequireRestaurant] Restaurantes encontrados:', restaurants.length);

      if (restaurants.length === 0) {
        console.log('[RequireRestaurant] Usuario sin restaurantes, redirigiendo a crear...');
        navigate('/restaurant/new', {
          state: {
            message: 'Bienvenido! Por favor crea tu primer restaurante para comenzar.',
          },
        });
        setHasRestaurants(false);
      } else {
        setHasRestaurants(true);
      }
    } catch (error) {
      console.error('[RequireRestaurant] Error verificando restaurantes:', error);
      // En caso de error, permitir continuar para no bloquear al usuario
      setHasRestaurants(true);
    } finally {
      setChecking(false);
    }
  };

  // Mientras verifica, mostrar loading o nada
  if (checking) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <p>{t('common.loading')}</p>
      </div>
    );
  }

  // Si no tiene restaurantes y no estamos en /restaurant/new, no renderizar nada
  // (el navigate ya habrá redirigido)
  if (!hasRestaurants && location.pathname !== '/restaurant/new') {
    return null;
  }

  return children;
}

export default RequireRestaurant;
